import Service from '@ember/service';

import EmberObject, { action } from '@ember/object';
import Evented from '@ember/object/evented';
import { once } from '@ember/runloop';
import { service } from '@ember/service';

import { call } from 'rise-ui/utils/function.js';
import { escape } from 'rise-ui/utils/escape.js';
import { IntersectionObserver } from 'rise-ui/utils/intersection-observer.js';

function escapePath(string) {
  return escape(string, '.');
}

function getPath(...parts) {
  return parts.map(part => escapePath(part)).join('.');
}

export default class ViewportService extends Service {
  @service ssr;

  events = EmberObject.extend(Evented).create();

  root = null;

  overlays = new Map();
  overlaySizes = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  };

  observer;
  observers = new Map();

  lastScrollPos = 0;
  isScrollingDirectionUp;

  constructor() {
    super(...arguments);
    if (this.ssr.isSsr) {
      return;
    }
    document.addEventListener('scroll', e => {
      if (this.lastScrollPos !== window.scrollY) {
        const isScrollingDirectionUp = this.lastScrollPos > window.scrollY;
        this.lastScrollPos = window.scrollY;
        if (isScrollingDirectionUp !== this.isScrollingDirectionUp) {
          this.isScrollingDirectionUp = isScrollingDirectionUp;
          once(this, '_onScrollDirectionChange');
        }
      }
    });
    this.updateObserver();
  }

  _onScrollDirectionChange() {
    this.events.trigger('scroll-direction', this.isScrollingDirectionUp);
  }

  observeScrollDirection(callback) {
    this.events.on('scroll-direction', callback);
  }

  unobserveScrollDirection(callback) {
    this.events.off('scroll-direction', callback);
  }

  addOverlay(name, side, size) {
    if (!(side in this.overlaySizes)) {
      throw new Error('Expected top, right, bottom, or left');
    }
    const key = getPath(name, side);
    let overlay = this.overlays.get(key);
    let prevSize = 0;
    if (!overlay) {
      this.overlays.set(key, overlay = { name, side, size });
    } else {
      prevSize = overlay.size;
    }
    overlay.size = size;
    if (prevSize !== size) {
      this.overlaySizes[side] += size - prevSize;
      this.updateObserver();
    }
  }

  updateOverlay(name, side, size) {
    return this.addOverlay(name, side, size);
  }

  removeOverlay(name, side) {
    const key = getPath(name, side);
    const overlay = this.overlays.get(key);
    if (!overlay) {
      return;
    }
    this.overlays.delete(key);
    const { size } = overlay;
    if (size) {
      this.overlaySizes[side] -= size;
      this.updateObserver();
    }
  }

  addObserver(el, callback) {
    this.observers.set(el, { callback });
    this.observer?.observe(el);
  }

  removeObserver(el) {
    this.observer?.unobserve(el);
    this.observers.delete(el);
  }

  @action onIntersection(entries) {
    entries.forEach(entry => {
      const { isIntersecting, target, boundingClientRect, rootBounds } = entry;
      const observer = this.observers.get(target);
      if (observer && rootBounds) {
        const { callback } = observer;
        const intersecting = `${isIntersecting}.${boundingClientRect.top < rootBounds.top}`;
        if (intersecting !== observer.intersection) {
          observer.intersection = intersecting;
          call(callback, null, entry);
        }
      }
    });
  }

  updateObserver() {
    const { top, right, bottom, left } = this.overlaySizes;
    const options = {
      root: this.root,
      rootMargin: `${-top}px ${-right}px ${-bottom}px ${-left}px`,
      threshold: 1,
    };
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(this.onIntersection, options);
    this.observers.forEach((callback, el) => this.observer.observe(el));
  }
}
